import { RangeHorizon } from '@toggle/chart';
import { Entity } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { ErrorMessageWrapper } from '~/components/error-message-wrapper/ErrorMessageWrapper';
import { SectionTitle } from '~/components/section-title/SectionTitle';
import { useCompanyCompetitors } from '~/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { appPaths } from '~/routes/app-paths';
import {
  getChartSubPageSearchParams,
  SnakeTypes,
} from '~/widgets/chart-peer-group/utils/chart-subpage-navigation/chart-subpage-navigation';

import { MultiLineChartModule } from '../multi-line-chart/MultiLineChartModule';
import * as S from './OverviewFinancials.styles';

export interface OverviewFinancialsProps {
  entity: Entity;
  isDrawer?: boolean;
}

const COMPETITORS_AMOUNT = 4;

export const OverviewFinancials = ({ entity, isDrawer = false }: OverviewFinancialsProps) => {
  const { t } = useTranslation('asset-overview');
  const [searchParams] = useSearchParams();

  const { competitorsData, isFetching, isError } = useCompanyCompetitors({
    entityTag: entity.tag,
    limit: COMPETITORS_AMOUNT,
  });

  if (isFetching) {
    return <S.SkeletonChartLoader />;
  }

  if (isError) {
    return <ErrorMessageWrapper displayIcon />;
  }

  const competitorEntities = competitorsData.map(({ entity }) => entity);
  const entities = [entity, ...competitorEntities];

  const chartSubPageSearchParams = getChartSubPageSearchParams({
    entityTag: entity.tag,
    prevSearchParams: searchParams,
    horizon: RangeHorizon.OneYear,
    snakeType: SnakeTypes.Price,
  }).toString();

  const linkToChartSubPage = `${appPaths.assetOverview}?${chartSubPageSearchParams}`;

  return (
    <S.Container $isDrawer={isDrawer}>
      <SectionTitle
        title={t('asset-overview:performance')}
        linkTo={linkToChartSubPage}
      />

      <S.Title>
        {t('asset-overview:last12Months')}
        <span>%</span>
      </S.Title>

      <MultiLineChartModule
        entities={entities}
        chartConfig={{
          enableZoom: false,
        }}
        tooltipSortConfig={{ field: 'priceFormatted', desc: true }}
      />
    </S.Container>
  );
};
